import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useEffect, useRef, useState, useCallback } from 'react';
import { useStepProgress } from 'src/hooks/useStepProgress';
import { useCheckClientFitnessProfile } from 'src/hooks/useCheckClientFitnessProfile';
import { useProgramGeneratorProgressStore } from 'src/store/programGeneratorProgressStore';
import { useGetPrograms } from 'src/api/queries/useGetPrograms';
import { useParams, useNavigate, useLocation, useSearchParams, useBlocker } from 'react-router-dom';
import MiniPTGIF from 'url:../../../../../../assets/videos/mini-pt-care.mp4';
import { TextAnimate } from 'src/components/Animation/text-animate';
import gsap from 'src/utils/gsapSetup';
import { Modal, Spin } from 'antd';
import { confirmationSVG } from './assets/Confirmation';
import { ReplaceProgramModal } from 'src/components/ReplaceProgramModal/ReplaceProgramModal';
import {
  fetchTrainerProgramPlanList,
  FetchedProgramPlan,
} from 'src/api/mutations/useGetTrainerProgramPlanList';
import { useDeleteProgramPlan } from 'src/api/mutations/useDeleteProgramPlan';
import { sessionStore } from 'src/store/sessionStore';
import { ClientSelectionStep } from './components/ClientSelectionStep';
import { EnergyLevelStep } from './components/EnergyLevelStep';
import { FitnessLevelStep } from './components/FitnessLevelStep';
import { ActivityLevelStep } from './components/ActivityLevelStep';
import { MovementInsightsStep } from './components/MovementInsightsStep';
import { OnboardNewClientPromptStep } from './components/OnboardNewClientPromptStep';
import { InjuriesLimitationsStep } from './components/InjuriesLimitationsStep';
import { ProgramDurationStep } from './components/ProgramDurationStep';
import { HowOftenTrainStep } from './components/HowOftenTrainStep';
import { WorkoutDurationStep } from './components/WorkoutDurationStep';
import { TrainingGoalStep } from './components/TrainingGoalStep';
import { IncreaseStrengthStep1 } from './components/IncreaseStrengthStep1';
import { IncreaseStrengthStep2 } from './components/IncreaseStrengthStep2';
import { IncreaseStrengthStep3 } from './components/IncreaseStrengthStep3';
import { EnduranceStep1 } from './components/EnduranceStep1';
import { EnduranceStep2 } from './components/EnduranceStep2';
import { PerformanceStep1 } from './components/PerformanceStep1';
import { PerformanceStep2 } from './components/PerformanceStep2';
import { PerformanceStep3 } from './components/PerformanceStep3';
import { GeneralFitnessStep1 } from './components/GeneralFitnessStep1';
import { GeneralFitnessStep2 } from './components/GeneralFitnessStep2';
import { InjuryPreventionStep1 } from './components/InjuryPreventionStep1';
import { InjuryPreventionStep2 } from './components/InjuryPreventionStep2';
import { InjuryPreventionStep3 } from './components/InjuryPreventionStep3';
import { RehabilitationStep1 } from './components/RehabilitationStep1';
import { RehabilitationStep2 } from './components/RehabilitationStep2';
import { RehabilitationStep3 } from './components/RehabilitationStep3';
import { MobilityStep1 } from './components/MobilityStep1';
import { MobilityStep2 } from './components/MobilityStep2';
import { MobilityStep3 } from './components/MobilityStep3';
import { BuildMuscleStep1 } from './components/BuildMuscleStep1';
import { BuildMuscleStep2 } from './components/BuildMuscleStep2';
import { BuildMuscleStep3 } from './components/BuildMuscleStep3';
import { WeightLossStep1 } from './components/WeightLossStep1';
import { WeightLossStep2 } from './components/WeightLossStep2';
import { WeightLossStep3 } from './components/WeightLossStep3';
import { WeightLossStep4 } from './components/WeightLossStep4';
import { SecondaryTrainingGoalStep } from './components/SecondaryTrainingGoalStep';
import { IntensityVibeStep } from './components/IntensityVibeStep';
import { SelectEquipmentStep } from './components/SelectEquipmentStep';
import { ReviewStep } from './components/ReviewStep';
import { ProgramNameStep } from './components/ProgramNameStep';
import { Button } from 'src/components/Button/Button';
import {
  PROGRAM_GENERATOR_SLUGS,
  MOVEMENT_INSIGHT_SLUGS,
  getSubStepSlugs,
  EQUIPMENT_MANDATORY_APPROACHES,
} from './constants';
import { ProgramStepId, TrainingGoalSlug } from './types';
import './ProgramGeneratorAI.scss';
import { ReactComponent as BackIcon } from '../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/back.svg';
import { ReactComponent as NextIcon } from '../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/next.svg';

export const ProgramGeneratorAI: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { by } = useParams<{ by: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const stepParam = searchParams.get('step');

  const goalParam = searchParams.get('goal');
  const subStepParam = searchParams.get('subStep');

  const getIndexFromSlug = useCallback(
    (goal: string | null, slug: string | null) => {
      if (!slug) return 1;
      let slugs: string[] = [];
      if (stepParam === 'movement-insights') {
        slugs = MOVEMENT_INSIGHT_SLUGS;
      } else if (goal) {
        slugs = getSubStepSlugs(goal);
      }
      const idx = slugs.indexOf(slug);
      return idx !== -1 ? idx + 1 : 1;
    },
    [stepParam],
  );

  const isCreatingRef = useRef(false);

  const urlProgramId = searchParams.get('programId');
  const onboardingOnly = searchParams.get('onboardingOnly') === 'true';
  const { data: programDataList, isLoading: isLoadingProgram } = useGetPrograms(
    { program_ids: urlProgramId ? [urlProgramId] : [] },
    { enabled: !!urlProgramId },
  );
  const draftProgram = programDataList?.[0];
  const [hasResumed, setHasResumed] = useState(false);

  const getNextStepInfo = useCallback((progress: Record<string, any[]>) => {
    if (!progress['energy_level'] || progress['energy_level'].length === 0) {
      return { step: 3, subStep: null };
    }
    if (!progress['activity_level'] || progress['activity_level'].length === 0) {
      return { step: 4, subStep: null };
    }
    if (!progress['fitness_level'] || progress['fitness_level'].length === 0) {
      return { step: 5, subStep: null };
    }
    const movementProgress = progress['movement_insights'] || [];
    if (movementProgress.length < 5) {
      const currentSubIndex = movementProgress.length + 1;
      const subSlug = MOVEMENT_INSIGHT_SLUGS[currentSubIndex - 1] || MOVEMENT_INSIGHT_SLUGS[0];
      return { step: 6, subStep: subSlug };
    }
    if (!progress['injuries_parts'] || progress['injuries_parts'].length === 0) {
      return { step: 7, subStep: null };
    }
    if (!progress['program_duration'] || progress['program_duration'].length === 0) {
      return { step: 8, subStep: null };
    }
    if (!progress['how_often_train'] || progress['how_often_train'].length === 0) {
      return { step: 10, subStep: null };
    }
    if (!progress['workout_duration'] || progress['workout_duration'].length === 0) {
      return { step: 11, subStep: null };
    }
    if (!progress['training_goal'] || progress['training_goal'].length === 0) {
      return { step: 12, subStep: null };
    }
    const goal = progress['training_goal']?.[0]?.value || '';
    const subSlugs = getSubStepSlugs(goal);
    const goalKeysMap: Record<string, string[]> = {
      [TrainingGoalSlug.IncreaseStrength]: [
        'increase_strength_focus',
        'increase_strength_outcome',
        'increase_strength_approach',
      ],
      [TrainingGoalSlug.BuildMuscle]: [
        'build_muscle_primary',
        'build_muscle_secondary',
        'build_muscle_approach',
      ],
      [TrainingGoalSlug.WeightLoss]: [
        'weight_loss_primary',
        'weight_loss_secondary',
        'weight_loss_target',
        'weight_loss_approach',
      ],
      [TrainingGoalSlug.Endurance]: ['endurance_type', 'endurance_approach'],
      [TrainingGoalSlug.Mobility]: ['mobility_area', 'mobility_purpose', 'mobility_approach'],
      [TrainingGoalSlug.Performance]: [
        'performance_support',
        'performance_focus',
        'performance_approach',
      ],
      [TrainingGoalSlug.GeneralFitness]: ['general_fitness_meaning', 'general_fitness_approach'],
      [TrainingGoalSlug.Rehabilitation]: [
        'rehabilitation_area',
        'rehabilitation_stage',
        'rehabilitation_approach',
      ],
      [TrainingGoalSlug.InjuryPrevention]: [
        'injury_prevention_area',
        'injury_prevention_focus',
        'injury_prevention_approach',
      ],
    };
    const keys = goalKeysMap[goal] || [];
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (!progress[key] || progress[key].length === 0) {
        const subSlug = subSlugs[i] || subSlugs[0];
        return { step: 13, subStep: subSlug };
      }
    }
    if (!progress['secondary_goals'] || progress['secondary_goals'].length === 0) {
      return { step: 14, subStep: null };
    }
    if (!progress['intensity'] || progress['intensity'].length === 0) {
      return { step: 15, subStep: null };
    }
    if (!progress['selected_equipment'] || progress['selected_equipment'].length === 0) {
      return { step: 16, subStep: null };
    }
    return { step: 17, subStep: null };
  }, []);

  const urlClientId = searchParams.get('clientId');
  const urlFrom = searchParams.get('from');
  const { checkProfile, isChecking: isCheckingProfile } = useCheckClientFitnessProfile();
  const [hasInitializedClient, setHasInitializedClient] = useState(false);
  const isHydratingProfileRef = useRef(!!urlClientId && !urlProgramId);

  const currentSlugIndex = PROGRAM_GENERATOR_SLUGS.indexOf(
    stepParam || (by === 'manual' ? 'client-selection' : 'intro'),
  );
  const [step, setStep] = useState(currentSlugIndex !== -1 ? currentSlugIndex + 1 : 1);

  const [currentSubStepIndex, setCurrentSubStepIndex] = useState(
    getIndexFromSlug(goalParam, subStepParam),
  );
  const [movementInsightIndex, setMovementInsightIndex] = useState(
    stepParam === 'movement-insights' ? getIndexFromSlug(null, subStepParam) : 1,
  );

  const navigateToStep = useCallback(
    (nextStepIndex: number) => {
      setStep(nextStepIndex);
      const slug = PROGRAM_GENERATOR_SLUGS[nextStepIndex - 1];
      if (slug) {
        const params = new URLSearchParams(searchParams);
        params.set('step', slug);
        // Clear subStep param if not on a step that supports it
        if (nextStepIndex !== 6 && nextStepIndex !== 13) {
          params.delete('subStep');
        }
        // We do NOT use replace: true so that moving forward creates a new history entry
        navigate(`${location.pathname}?${params.toString()}`);
      }
    },
    [searchParams, navigate, location.pathname, setStep],
  );

  useEffect(() => {
    if (draftProgram && !hasResumed) {
      const initialize = async () => {
        const store = useProgramGeneratorProgressStore.getState();
        store.setProgramId(draftProgram.id);
        store.setClientId(draftProgram.client_id);
        if (draftProgram.progress) {
          store.setProgress(draftProgram.progress);
        }

        // Fetch the fitness profile so the store has BOTH program and fitness data
        if (draftProgram.client_id) {
          await checkProfile(draftProgram.client_id);
        }

        const combinedProgress = useProgramGeneratorProgressStore.getState().progress;
        const nextInfo = getNextStepInfo(combinedProgress);
        const nextSlug = PROGRAM_GENERATOR_SLUGS[nextInfo.step - 1];

        setHasResumed(true);

        const isEditMode = searchParams.get('editMode') === 'true' || !!searchParams.get('step');
        if (!isEditMode) {
          const params = new URLSearchParams(searchParams);
          params.set('step', nextSlug);
          if (nextInfo.subStep) {
            params.set('subStep', nextInfo.subStep);
          } else {
            params.delete('subStep');
          }
          if (combinedProgress?.['training_goal']?.[0]?.value) {
            params.set('goal', combinedProgress['training_goal'][0].value);
          }
          navigate(`${location.pathname}?${params.toString()}`, { replace: true });
        }
      };

      initialize();
    }
  }, [
    draftProgram,
    hasResumed,
    navigate,
    location.pathname,
    searchParams,
    getNextStepInfo,
    checkProfile,
  ]);

  useEffect(() => {
    if (urlClientId && !urlProgramId && !hasInitializedClient) {
      const isEditMode = searchParams.get('editMode') === 'true' || !!stepParam;
      const isFromClientProfile = urlFrom === 'client-profile';
      const store = useProgramGeneratorProgressStore.getState();
      if (store.clientId !== urlClientId || isFromClientProfile) {
        if (store.clientId !== urlClientId) {
          store.setProgress({});
        }
        store.setClientId(urlClientId);
      }
      setHasInitializedClient(true);
      isHydratingProfileRef.current = true;
      checkProfile(urlClientId).then((_nextStep) => {
        isHydratingProfileRef.current = false;
        if (!isEditMode && !stepParam) {
          navigateToStep(_nextStep);
        }
      });
    }
  }, [
    urlClientId,
    urlProgramId,
    hasInitializedClient,
    checkProfile,
    searchParams,
    stepParam,
    urlFrom,
    navigateToStep,
  ]);

  // Intercept native browser back button (and other route exits)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      step > 2 &&
      !isCreatingRef.current &&
      (currentLocation.pathname !== nextLocation.pathname ||
        (nextLocation.search.includes('action=new_program') &&
          !currentLocation.search.includes('action=new_program'))) &&
      !nextLocation.pathname.includes('/completed') &&
      !nextLocation.pathname.includes('/program-generated'),
  );

  const navigateToSubStep = useCallback(
    (goal: string | null, nextIdx: number) => {
      if (step === 6) {
        setMovementInsightIndex(nextIdx);
      } else {
        setCurrentSubStepIndex(nextIdx);
      }

      const params = new URLSearchParams(searchParams);
      const subSlugs = step === 6 ? MOVEMENT_INSIGHT_SLUGS : goal ? getSubStepSlugs(goal) : [];
      const subSlug = subSlugs[nextIdx - 1];

      if (subSlug) {
        params.set('subStep', subSlug);
        // We do NOT use replace: true so that moving forward creates a new history entry
        navigate(`${location.pathname}?${params.toString()}`);
      } else {
        navigate(`${location.pathname}?${params.toString()}`);
      }
    },
    [
      step,
      searchParams,
      navigate,
      location.pathname,
      setMovementInsightIndex,
      setCurrentSubStepIndex,
    ],
  );
  const [isLoading] = useState(false);

  const clientId = useProgramGeneratorProgressStore((state) => state.clientId);
  const [clientName, setClientName] = useStepProgress<string>(
    ProgramStepId.ClientName,
    'Client',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || 'Client',
  );
  const [clientBodyProfile, setClientBodyProfile] = useStepProgress<string>(
    ProgramStepId.ClientBodyProfile,
    'male',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || 'male',
  );

  const [trainingGoal, setTrainingGoal] = useStepProgress<string>(
    ProgramStepId.TrainingGoal,
    goalParam || '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || goalParam || '',
  );

  // Handle 'new_program' action which comes from sidebar 'Create (+)' click
  useEffect(() => {
    if (searchParams.get('action') === 'new_program') {
      setTimeout(() => {
        useProgramGeneratorProgressStore.getState().resetProgress();
        const params = new URLSearchParams(searchParams);
        params.delete('action');
        params.delete('subStep');
        params.delete('goal');
        params.set('step', 'client-selection');
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
        setStep(1);
      }, 0);
    }
  }, [searchParams, location.pathname, navigate]);

  // Sync state with URL when user navigates using browser back/forward buttons
  useEffect(() => {
    const newSlugIndex = PROGRAM_GENERATOR_SLUGS.indexOf(
      stepParam || (by === 'manual' ? 'client-selection' : 'intro'),
    );
    if (newSlugIndex !== -1 && newSlugIndex + 1 !== step) {
      setStep(newSlugIndex + 1);
    }

    if (stepParam === 'movement-insights') {
      const idx = getIndexFromSlug(null, subStepParam);
      if (idx !== movementInsightIndex) {
        setMovementInsightIndex(idx);
      }
    } else {
      const idx = getIndexFromSlug(trainingGoal, subStepParam);
      if (idx !== currentSubStepIndex) {
        setCurrentSubStepIndex(idx);
      }
    }
  }, [
    stepParam,
    subStepParam,
    trainingGoal,
    by,
    movementInsightIndex,
    currentSubStepIndex,
    step,
    getIndexFromSlug,
  ]);
  const [increaseStrengthFocus, setIncreaseStrengthFocus] = useStepProgress<string[]>(
    ProgramStepId.IncreaseStrengthFocus,
    [],
    (vals: string[]) => vals.map((v: string) => ({ slug: v, value: v })),
    (items: any[]) => items.map((i: any) => i.value) || [],
  );
  const [increaseStrengthOutcome, setIncreaseStrengthOutcome] = useStepProgress<string>(
    ProgramStepId.IncreaseStrengthOutcome,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );
  const [increaseStrengthApproach, setIncreaseStrengthApproach] = useStepProgress<string>(
    ProgramStepId.IncreaseStrengthApproach,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );

  const [enduranceType, setEnduranceType] = useStepProgress<string>(
    ProgramStepId.EnduranceType,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );
  const [enduranceApproach, setEnduranceApproach] = useStepProgress<string>(
    ProgramStepId.EnduranceApproach,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );

  const [programName, setProgramName] = useStepProgress<string>(
    ProgramStepId.ProgramName,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );

  const [performanceSupport, setPerformanceSupport] = useStepProgress<string>(
    ProgramStepId.PerformanceSupport,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );
  const [performanceFocus, setPerformanceFocus] = useStepProgress<string>(
    ProgramStepId.PerformanceFocus,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );
  const [performanceApproach, setPerformanceApproach] = useStepProgress<string>(
    ProgramStepId.PerformanceApproach,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );

  const [generalFitnessMeaning, setGeneralFitnessMeaning] = useStepProgress<string>(
    ProgramStepId.GeneralFitnessMeaning,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );
  const [generalFitnessApproach, setGeneralFitnessApproach] = useStepProgress<string>(
    ProgramStepId.GeneralFitnessApproach,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );

  const [injuryPreventionArea, setInjuryPreventionArea] = useStepProgress<string[]>(
    ProgramStepId.InjuryPreventionArea,
    [],
    (vals: string[]) => vals.map((v: string) => ({ slug: v, value: v })),
    (items: any[]) => items.map((i: any) => i.value) || [],
  );
  const [injuryPreventionFocus, setInjuryPreventionFocus] = useStepProgress<string>(
    ProgramStepId.InjuryPreventionFocus,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );
  const [injuryPreventionApproach, setInjuryPreventionApproach] = useStepProgress<string>(
    ProgramStepId.InjuryPreventionApproach,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );

  const [rehabilitationArea, setRehabilitationArea] = useStepProgress<string[]>(
    ProgramStepId.RehabilitationArea,
    [],
    (vals: string[]) => vals.map((v: string) => ({ slug: v, value: v })),
    (items: any[]) => items.map((i: any) => i.value) || [],
  );
  const [rehabilitationStage, setRehabilitationStage] = useStepProgress<string>(
    ProgramStepId.RehabilitationStage,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );
  const [rehabilitationApproach, setRehabilitationApproach] = useStepProgress<string>(
    ProgramStepId.RehabilitationApproach,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );

  const [mobilityArea, setMobilityArea] = useStepProgress<string[]>(
    ProgramStepId.MobilityArea,
    [],
    (vals: string[]) => vals.map((v: string) => ({ slug: v, value: v })),
    (items: any[]) => items.map((i: any) => i.value) || [],
  );
  const [mobilityPurpose, setMobilityPurpose] = useStepProgress<string>(
    ProgramStepId.MobilityPurpose,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );
  const [mobilityApproach, setMobilityApproach] = useStepProgress<string>(
    ProgramStepId.MobilityApproach,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );

  const [buildMusclePrimary, setBuildMusclePrimary] = useStepProgress<string[]>(
    ProgramStepId.BuildMusclePrimary,
    [],
    (vals: string[]) => vals.map((v: string) => ({ slug: v, value: v })),
    (items: any[]) => items.map((i: any) => i.value) || [],
  );
  const [buildMuscleSecondary, setBuildMuscleSecondary] = useStepProgress<string[]>(
    ProgramStepId.BuildMuscleSecondary,
    [],
    (vals: string[]) => vals.map((v: string) => ({ slug: v, value: v })),
    (items: any[]) => items.map((i: any) => i.value) || [],
  );
  const [buildMuscleApproach, setBuildMuscleApproach] = useStepProgress<string>(
    ProgramStepId.BuildMuscleApproach,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );

  const [weightLossPrimary, setWeightLossPrimary] = useStepProgress<string>(
    ProgramStepId.WeightLossPrimary,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );
  const [weightLossSecondary, setWeightLossSecondary] = useStepProgress<string[]>(
    ProgramStepId.WeightLossSecondary,
    [],
    (vals: string[]) => vals.map((v: string) => ({ slug: v, value: v })),
    (items: any[]) => items.map((i: any) => i.value) || [],
  );
  const [weightLossTarget, setWeightLossTarget] = useStepProgress<{ target: number; unit: string }>(
    ProgramStepId.WeightLossTarget,
    {
      target: 45,
      unit: 'Kg',
    },
    (val: any) => [{ slug: String(val.target), value: val.unit }],
    (items) =>
      items[0]
        ? { target: Number(items[0].slug), unit: items[0].value }
        : {
            target: 45,
            unit: 'Kg',
          },
  );
  const [weightLossApproach, setWeightLossApproach] = useStepProgress<string>(
    ProgramStepId.WeightLossApproach,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );

  const [secondaryGoals, setSecondaryGoals] = useStepProgress<string[]>(
    ProgramStepId.SecondaryGoals,
    [],
    (vals: string[]) => vals.map((v: string) => ({ slug: v, value: v })),
    (items: any[]) => items.map((i: any) => i.value) || [],
  );
  const [selectedEquipment, setSelectedEquipment] = useStepProgress<string[]>(
    ProgramStepId.SelectedEquipment,
    [],
    (vals: string[]) => vals.map((v: string) => ({ slug: v, value: v })),
    (items: any[]) => items.map((i: any) => i.value) || [],
  );
  const [injuriesParts, setInjuriesParts] = useStepProgress<string[]>(
    ProgramStepId.InjuriesParts,
    [],
    (vals: string[]) => vals.map((v: string) => ({ slug: v, value: v })),
    (items: any[]) => items.map((i: any) => i.value) || [],
  );
  const [injuriesNotes, setInjuriesNotes] = useStepProgress<string>(
    ProgramStepId.InjuriesNotes,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );

  // Lifted states
  const [energyLevel, setEnergyLevel] = useStepProgress<string>(
    ProgramStepId.EnergyLevel,
    'often-low',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || 'often-low',
  );
  const [activityLevel, setActivityLevel] = useStepProgress<number>(
    ProgramStepId.ActivityLevel,
    10,
    (val: number) => [{ slug: String(val), value: String(val) }],
    (items: any[]) => (items[0]?.value ? Number(items[0].value) : 10),
  );
  const [fitnessLevel, setFitnessLevel] = useStepProgress<string>(
    ProgramStepId.FitnessLevel,
    'beginner',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => (items[0]?.value ? items[0].value : 'beginner'),
  );

  const [movementInsights, setMovementInsights] = useStepProgress<string[]>(
    ProgramStepId.MovementInsights,
    ['pain', 'pain', 'pain', 'pain', 'pain'],
    (vals: string[]) =>
      vals.map((v: string, i: number) => ({
        slug: MOVEMENT_INSIGHT_SLUGS[i] || String(i),
        value: v,
      })),
    (items) =>
      items.length > 0 ? items.map((i) => i.value) : ['pain', 'pain', 'pain', 'pain', 'pain'],
  );
  const [programDuration, setProgramDuration] = useStepProgress<number>(
    ProgramStepId.ProgramDuration,
    1,
    (val: number) => [
      { slug: `${val}-week${val > 1 ? 's' : ''}`, value: `${val}-week${val > 1 ? 's' : ''}` },
    ],
    (items: any[]) => (items[0]?.value ? parseInt(items[0].value, 10) : 1),
  );
  const [howOftenTrain, setHowOftenTrain] = useStepProgress<string[]>(
    ProgramStepId.HowOftenTrain,
    [],
    (vals: string[]) => vals.map((v: string) => ({ slug: v, value: v })),
    (items: any[]) => items.map((i: any) => i.value) || [],
  );
  const [howOftenTrainReminder, setHowOftenTrainReminder] = useStepProgress<string>(
    ProgramStepId.HowOftenTrainReminder,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );
  const [workoutDuration, setWorkoutDuration] = useStepProgress<string>(
    ProgramStepId.WorkoutDuration,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );
  const [intensity, setIntensity] = useStepProgress<string>(
    ProgramStepId.Intensity,
    '',
    (val: string) => [{ slug: val, value: val }],
    (items: any[]) => items[0]?.value || '',
  );
  const [isEditingFromReview, setIsEditingFromReview] = useState<boolean>(false);
  const [showReplaceProgramModal, setShowReplaceProgramModal] = useState<boolean>(false);
  const [clientPrograms, setClientPrograms] = useState<FetchedProgramPlan[]>([]);
  const [isReplacingProgram, setIsReplacingProgram] = useState<boolean>(false);
  const [isCheckingPrograms, setIsCheckingPrograms] = useState<boolean>(false);

  const currentUser = sessionStore((state) => state.currentUser);
  const trainerId = currentUser?.uid;
  const { mutateAsync: deleteProgram } = useDeleteProgramPlan();

  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Calculate progress percentage assuming 17 steps max for now
  const totalSteps = 17;

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = true;
      video.defaultMuted = true;
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    }
  }, [step]);

  useEffect(() => {
    if (videoContainerRef.current) {
      if (step >= 2 && step !== 8) {
        gsap.set(videoContainerRef.current, {
          top: '40px',
          y: 0,
          yPercent: 0,
          scale: 1,
          width: 100,
          height: 100,
        });
      }
    }
  }, [by, step, isLoadingProgram, hasResumed]);

  useEffect(() => {
    if (step === 8 && videoContainerRef.current) {
      gsap.to(videoContainerRef.current, {
        top: 'calc(50% - 80px)',
        y: 0,
        yPercent: -50,
        scale: 1,
        width: 250,
        height: 250,
        duration: 1,
        ease: 'power3.inOut',
      });
      gsap.to('.animation-content-8', { opacity: 1, duration: 0.5 });
    }
  }, [step]);

  const isEquipmentMandatory = useCallback((): boolean => {
    let currentApproach = '';
    switch (trainingGoal) {
      case TrainingGoalSlug.IncreaseStrength:
        currentApproach = increaseStrengthApproach;
        break;
      case TrainingGoalSlug.BuildMuscle:
        currentApproach = buildMuscleApproach;
        break;
      case TrainingGoalSlug.WeightLoss:
        currentApproach = weightLossApproach;
        break;
      case TrainingGoalSlug.Endurance:
        currentApproach = enduranceApproach;
        break;
      case TrainingGoalSlug.Mobility:
        currentApproach = mobilityApproach;
        break;
      case TrainingGoalSlug.Performance:
        currentApproach = performanceApproach;
        break;
      case TrainingGoalSlug.GeneralFitness:
        currentApproach = generalFitnessApproach;
        break;
      case TrainingGoalSlug.Rehabilitation:
        currentApproach = rehabilitationApproach;
        break;
      case TrainingGoalSlug.InjuryPrevention:
        currentApproach = injuryPreventionApproach;
        break;
    }
    return EQUIPMENT_MANDATORY_APPROACHES.includes(currentApproach);
  }, [
    trainingGoal,
    increaseStrengthApproach,
    buildMuscleApproach,
    weightLossApproach,
    enduranceApproach,
    mobilityApproach,
    performanceApproach,
    generalFitnessApproach,
    rehabilitationApproach,
    injuryPreventionApproach,
  ]);

  const getEarliestIncompleteStep = useCallback((): {
    stepIndex: number;
    subStepIndex?: number;
  } | null => {
    const checkPast = (minStep: number) => step > minStep || isEditingFromReview;
    const isManual = by === 'manual';

    if (isManual) {
      if (checkPast(2) && !clientId) return { stepIndex: 2 };
      if (checkPast(9) && !programDuration) return { stepIndex: 9 };
      if (checkPast(10) && (howOftenTrain.length === 0 || !howOftenTrainReminder))
        return { stepIndex: 10 };
      return null;
    }

    if (checkPast(2) && !clientId && by !== 'manual') return { stepIndex: 2 };
    if (checkPast(3) && !energyLevel) return { stepIndex: 3 };
    if (checkPast(7) && injuriesNotes.trim() !== '' && !/\p{L}/u.test(injuriesNotes))
      return { stepIndex: 7 };

    if (checkPast(10) && (howOftenTrain.length === 0 || !howOftenTrainReminder))
      return { stepIndex: 10 };
    if (checkPast(11) && !workoutDuration) return { stepIndex: 11 };
    if (checkPast(12) && !trainingGoal) return { stepIndex: 12 };

    if (checkPast(12) || step === 13) {
      const isPast = (idx: number) => checkPast(13) || currentSubStepIndex > idx;
      if (trainingGoal === TrainingGoalSlug.IncreaseStrength) {
        if (isPast(1) && increaseStrengthFocus.length === 0)
          return { stepIndex: 13, subStepIndex: 1 };
        if (isPast(2) && !increaseStrengthOutcome) return { stepIndex: 13, subStepIndex: 2 };
        if (isPast(3) && !increaseStrengthApproach) return { stepIndex: 13, subStepIndex: 3 };
      } else if (trainingGoal === TrainingGoalSlug.BuildMuscle) {
        if (isPast(1) && buildMusclePrimary.length === 0) return { stepIndex: 13, subStepIndex: 1 };
        if (isPast(2) && buildMuscleSecondary.length === 0)
          return { stepIndex: 13, subStepIndex: 2 };
        if (isPast(3) && !buildMuscleApproach) return { stepIndex: 13, subStepIndex: 3 };
      } else if (trainingGoal === TrainingGoalSlug.WeightLoss) {
        if (isPast(1) && !weightLossPrimary) return { stepIndex: 13, subStepIndex: 1 };
        if (isPast(2) && weightLossSecondary.length === 0)
          return { stepIndex: 13, subStepIndex: 2 };
        if (isPast(3) && !weightLossTarget) return { stepIndex: 13, subStepIndex: 3 };
        if (isPast(4) && !weightLossApproach) return { stepIndex: 13, subStepIndex: 4 };
      } else if (trainingGoal === TrainingGoalSlug.Endurance) {
        if (isPast(1) && !enduranceType) return { stepIndex: 13, subStepIndex: 1 };
        if (isPast(2) && !enduranceApproach) return { stepIndex: 13, subStepIndex: 2 };
      } else if (trainingGoal === TrainingGoalSlug.Mobility) {
        if (isPast(1) && mobilityArea.length === 0) return { stepIndex: 13, subStepIndex: 1 };
        if (isPast(2) && !mobilityPurpose) return { stepIndex: 13, subStepIndex: 2 };
        if (isPast(3) && !mobilityApproach) return { stepIndex: 13, subStepIndex: 3 };
      } else if (trainingGoal === TrainingGoalSlug.Performance) {
        if (isPast(1) && !performanceSupport) return { stepIndex: 13, subStepIndex: 1 };
        if (isPast(2) && !performanceFocus) return { stepIndex: 13, subStepIndex: 2 };
        if (isPast(3) && !performanceApproach) return { stepIndex: 13, subStepIndex: 3 };
      } else if (trainingGoal === TrainingGoalSlug.GeneralFitness) {
        if (isPast(1) && !generalFitnessMeaning) return { stepIndex: 13, subStepIndex: 1 };
        if (isPast(2) && !generalFitnessApproach) return { stepIndex: 13, subStepIndex: 2 };
      } else if (trainingGoal === TrainingGoalSlug.Rehabilitation) {
        if (isPast(1)) {
          let isRehabAreaValid = true;
          if (rehabilitationArea.length === 0) isRehabAreaValid = false;
          else {
            const otherVal = rehabilitationArea.find((v: string) => v.startsWith('other:'));
            if (otherVal) {
              const text = otherVal.replace('other:', '').trim();
              if (text === '' || !/\p{L}/u.test(text)) isRehabAreaValid = false;
            }
          }
          if (!isRehabAreaValid) return { stepIndex: 13, subStepIndex: 1 };
        }
        if (isPast(2) && !rehabilitationStage) return { stepIndex: 13, subStepIndex: 2 };
        if (isPast(3) && !rehabilitationApproach) return { stepIndex: 13, subStepIndex: 3 };
      } else if (trainingGoal === TrainingGoalSlug.InjuryPrevention) {
        if (isPast(1) && injuryPreventionArea.length === 0)
          return { stepIndex: 13, subStepIndex: 1 };
        if (isPast(2) && !injuryPreventionFocus) return { stepIndex: 13, subStepIndex: 2 };
        if (isPast(3) && !injuryPreventionApproach) return { stepIndex: 13, subStepIndex: 3 };
      }
    }

    if (checkPast(15) && !intensity) return { stepIndex: 15 };
    if (checkPast(16) && isEquipmentMandatory() && selectedEquipment.length === 0)
      return { stepIndex: 16 };
    return null;
  }, [
    step,
    currentSubStepIndex,
    clientId,
    programDuration,
    energyLevel,
    injuriesNotes,
    howOftenTrain.length,
    howOftenTrainReminder,
    workoutDuration,
    trainingGoal,
    increaseStrengthFocus,
    increaseStrengthOutcome,
    increaseStrengthApproach,
    buildMusclePrimary.length,
    buildMuscleSecondary.length,
    buildMuscleApproach,
    weightLossPrimary,
    weightLossSecondary.length,
    weightLossTarget,
    weightLossApproach,
    enduranceType,
    enduranceApproach,
    mobilityArea,
    mobilityPurpose,
    mobilityApproach,
    performanceSupport,
    performanceFocus,
    performanceApproach,
    generalFitnessMeaning,
    generalFitnessApproach,
    rehabilitationArea,
    rehabilitationStage,
    rehabilitationApproach,
    injuryPreventionArea,
    injuryPreventionFocus,
    injuryPreventionApproach,
    intensity,
    selectedEquipment.length,
    isEquipmentMandatory,
    isEditingFromReview,
    by,
  ]);

  const hasIncompleteStepAhead = useCallback(() => {
    const incomplete = getEarliestIncompleteStep();
    if (!incomplete) return false;
    if (incomplete.stepIndex > step) return true;
    if (
      incomplete.stepIndex === step &&
      incomplete.subStepIndex &&
      incomplete.subStepIndex > currentSubStepIndex
    )
      return true;
    return false;
  }, [getEarliestIncompleteStep, step, currentSubStepIndex]);

  // Redirect to earliest incomplete step if user skips ahead via URL
  useEffect(() => {
    const isEditMode = searchParams.get('editMode') === 'true' || !!stepParam;
    // Only enforce this if we are not actively loading or editing
    if (
      isLoading ||
      isEditingFromReview ||
      isCheckingProfile ||
      isHydratingProfileRef.current ||
      isEditMode
    )
      return;

    const incomplete = getEarliestIncompleteStep();
    if (incomplete) {
      if (incomplete.stepIndex === 2 && !clientId && by !== 'manual') {
        navigateToStep(1); // Redirect AI flow to Intro instead of client selection
      } else if (incomplete.subStepIndex) {
        // Just directly navigate to the missing sub step
        setStep(13);
        setCurrentSubStepIndex(incomplete.subStepIndex);
        const slug = PROGRAM_GENERATOR_SLUGS[12];
        const params = new URLSearchParams(searchParams);
        params.set('step', slug);
        const subSlugs = getSubStepSlugs(trainingGoal);
        const subSlug = subSlugs[incomplete.subStepIndex - 1];
        if (subSlug) params.set('subStep', subSlug);
        navigate(`${location.pathname}?${params.toString()}`);
      } else {
        navigateToStep(incomplete.stepIndex);
      }
    }
  }, [
    getEarliestIncompleteStep,
    isLoading,
    isCheckingProfile,
    isEditingFromReview,
    by,
    navigate,
    location.pathname,
    searchParams,
    trainingGoal,
    clientId,
    navigateToStep,
    stepParam,
  ]);

  const handleAnimationComplete = () => {
    setTimeout(() => {
      // Fade out the step 1 text and orb text
      gsap.to('.animation-content, .mini-pt-text', { opacity: 0, duration: 0.5 });

      // Animate the main orb to the top of the screen
      gsap.to(videoContainerRef.current, {
        top: '40px',
        y: 0,
        yPercent: 0,
        scale: 1,
        width: 100,
        height: 100,
        duration: 1,
        ease: 'power3.inOut',
        onComplete: () => {
          navigateToStep(2);
        },
      });
    }, 1500);
  };

  const handleAnimation8Complete = () => {
    setTimeout(() => {
      gsap.to('.animation-content-8', { opacity: 0, duration: 0.5 });

      gsap.to(videoContainerRef.current, {
        top: '40px',
        y: 0,
        yPercent: 0,
        scale: 1,
        width: 100,
        height: 100,
        duration: 1,
        ease: 'power3.inOut',
        onComplete: () => {
          navigateToStep(9);
        },
      });
    }, 1500);
  };

  const handleNext = (selectedGoalOrClientId?: string | React.MouseEvent) => {
    // Disable if already loading
    if (isLoading || isCreatingRef.current || isCheckingProfile || isCheckingPrograms) return;

    // Force save the current state for steps with default values in case the user didn't change them
    if (step === 3) setEnergyLevel((prev) => prev);
    if (step === 4) setActivityLevel((prev) => prev);
    if (step === 5) setFitnessLevel((prev) => prev);
    if (step === 6) setMovementInsights((prev) => prev);
    if (step === 7) {
      setInjuriesParts((prev) => prev);
    }
    if (step === 9) setProgramDuration((prev) => prev);
    if (step === 10) {
      setHowOftenTrain((prev) => prev);
      setHowOftenTrainReminder((prev) => prev);
      if (by === 'manual') {
        navigateToStep(18);
        return;
      }
    }

    if (step === 18) {
      setProgramName((prev) => prev);
      const programId = useProgramGeneratorProgressStore.getState().programId;
      setTimeout(() => {
        navigate(`/onboarding-trainer/dashboard/program-generated/${programId}?isManually=true`);
      }, 300);
      return;
    }

    if (step === 11) setWorkoutDuration((prev) => prev);

    const currentGoal =
      typeof selectedGoalOrClientId === 'string' ? selectedGoalOrClientId : trainingGoal;

    if (step === 12) setTrainingGoal(currentGoal);
    if (step === 13) {
      if (currentGoal === TrainingGoalSlug.IncreaseStrength) {
        if (currentSubStepIndex === 1) setIncreaseStrengthFocus((prev) => prev);
        if (currentSubStepIndex === 2) setIncreaseStrengthOutcome((prev) => prev);
        if (currentSubStepIndex === 3) setIncreaseStrengthApproach((prev) => prev);
      } else if (currentGoal === TrainingGoalSlug.BuildMuscle) {
        if (currentSubStepIndex === 1) setBuildMusclePrimary((prev) => prev);
        if (currentSubStepIndex === 2) setBuildMuscleSecondary((prev) => prev);
        if (currentSubStepIndex === 3) setBuildMuscleApproach((prev) => prev);
      } else if (currentGoal === TrainingGoalSlug.WeightLoss) {
        if (currentSubStepIndex === 1) setWeightLossPrimary((prev) => prev);
        if (currentSubStepIndex === 2) setWeightLossSecondary((prev) => prev);
        if (currentSubStepIndex === 3) setWeightLossTarget((prev) => prev);
        if (currentSubStepIndex === 4) setWeightLossApproach((prev) => prev);
      } else if (currentGoal === TrainingGoalSlug.Endurance) {
        if (currentSubStepIndex === 1) setEnduranceType((prev) => prev);
        if (currentSubStepIndex === 2) setEnduranceApproach((prev) => prev);
      } else if (currentGoal === TrainingGoalSlug.Mobility) {
        if (currentSubStepIndex === 1) setMobilityArea((prev) => prev);
        if (currentSubStepIndex === 2) setMobilityPurpose((prev) => prev);
        if (currentSubStepIndex === 3) setMobilityApproach((prev) => prev);
      } else if (currentGoal === TrainingGoalSlug.Performance) {
        if (currentSubStepIndex === 1) setPerformanceSupport((prev) => prev);
        if (currentSubStepIndex === 2) setPerformanceFocus((prev) => prev);
        if (currentSubStepIndex === 3) setPerformanceApproach((prev) => prev);
      } else if (currentGoal === TrainingGoalSlug.GeneralFitness) {
        if (currentSubStepIndex === 1) setGeneralFitnessMeaning((prev) => prev);
        if (currentSubStepIndex === 2) setGeneralFitnessApproach((prev) => prev);
      } else if (currentGoal === TrainingGoalSlug.Rehabilitation) {
        if (currentSubStepIndex === 1) setRehabilitationArea((prev) => prev);
        if (currentSubStepIndex === 2) setRehabilitationStage((prev) => prev);
        if (currentSubStepIndex === 3) setRehabilitationApproach((prev) => prev);
      } else if (currentGoal === TrainingGoalSlug.InjuryPrevention) {
        if (currentSubStepIndex === 1) setInjuryPreventionArea((prev) => prev);
        if (currentSubStepIndex === 2) setInjuryPreventionFocus((prev) => prev);
        if (currentSubStepIndex === 3) setInjuryPreventionApproach((prev) => prev);
      }
    }

    if (step === 14) setSecondaryGoals((prev) => prev);
    if (step === 15) setIntensity((prev) => prev);
    if (step === 16) setSelectedEquipment((prev) => prev);

    if (step === 2) {
      const explicitClientId =
        typeof selectedGoalOrClientId === 'string' ? selectedGoalOrClientId : undefined;
      const targetClientId =
        explicitClientId || clientId || useProgramGeneratorProgressStore.getState().clientId;

      if (!targetClientId) return;

      const handleProfileCheck = async (cid: string) => {
        setIsCheckingPrograms(true);
        try {
          if (trainerId) {
            const programs = await fetchTrainerProgramPlanList(trainerId as string, {
              clientId: cid,
              isActive: '1',
            });
            const activeClientPrograms = (programs || []).filter(
              (p) => (p.clientId === cid || !p.clientId) && String(p.isActive) === '1',
            );
            if (activeClientPrograms.length > 0) {
              setClientPrograms(activeClientPrograms);
              setShowReplaceProgramModal(true);
              setIsCheckingPrograms(false);
              return; // Wait for modal response
            }
          }
          if (by === 'manual') {
            navigateToStep(9);
            return;
          }
          const nextStep = await checkProfile(cid);
          navigateToStep(Math.min(nextStep, totalSteps));
        } catch (err) {
          console.error('Failed to check programs', err);
          const nextStep = await checkProfile(cid);
          navigateToStep(Math.min(nextStep, totalSteps));
        } finally {
          setIsCheckingPrograms(false);
        }
      };

      handleProfileCheck(targetClientId);
      return;
    }

    if (step === 6 && movementInsightIndex < 5) {
      navigateToSubStep(null, movementInsightIndex + 1);
    } else if (step === 13) {
      const maxSubSteps = getSubStepSlugs(currentGoal).length;
      if (currentSubStepIndex < maxSubSteps) {
        navigateToSubStep(currentGoal, currentSubStepIndex + 1);
      } else {
        navigateToStep(Math.min(step + 1, totalSteps));
      }
    } else if (
      step === 12 &&
      currentGoal !== TrainingGoalSlug.IncreaseStrength &&
      currentGoal !== TrainingGoalSlug.Endurance &&
      currentGoal !== TrainingGoalSlug.Performance &&
      currentGoal !== TrainingGoalSlug.GeneralFitness &&
      currentGoal !== TrainingGoalSlug.InjuryPrevention &&
      currentGoal !== TrainingGoalSlug.Rehabilitation &&
      currentGoal !== TrainingGoalSlug.Mobility &&
      currentGoal !== TrainingGoalSlug.BuildMuscle &&
      currentGoal !== TrainingGoalSlug.WeightLoss
    ) {
      // For now, other goals might not have extra steps, just advance
      navigateToStep(Math.min(step + 1, totalSteps));
    } else {
      if (step === 1) {
        handleAnimationComplete();
      } else if (urlFrom === 'client-profile' && (step === 6 || step === 7)) {
        const cid = useProgramGeneratorProgressStore.getState().clientId || urlClientId;
        queryClient.invalidateQueries({ queryKey: ['clientProfileTab'] });
        queryClient.invalidateQueries({ queryKey: ['clientOverview'] });

        // Add a slight delay before navigating to ensure backend processing completes
        setTimeout(() => {
          if (cid) {
            navigate(`/onboarding-trainer/dashboard/clients/${cid}/client-profile`);
          } else {
            navigate('/onboarding-trainer/dashboard/clients');
          }
        }, 500);
      } else if (step === 7 && onboardingOnly) {
        navigate('/onboarding-trainer/dashboard/clients');
      } else {
        navigateToStep(Math.min(step + 1, totalSteps));
      }
    }
  };
  const handleEditStep = (stepIndex: number) => {
    navigateToStep(stepIndex);
    setIsEditingFromReview(true);
  };

  const handleSaveEdit = () => {
    navigateToStep(17);
    setIsEditingFromReview(false);
  };

  const handleBack = () => {
    if (isLoading) return;

    if (step === 9) {
      if (by === 'manual') {
        navigateToStep(2);
      } else {
        navigateToStep(7);
      }
    } else if (step === 6 && movementInsightIndex > 1) {
      navigateToSubStep(null, movementInsightIndex - 1);
    } else if (step === 13 && currentSubStepIndex > 1) {
      navigateToSubStep(trainingGoal, currentSubStepIndex - 1);
    } else if (step === 2 && by === 'manual') {
      navigate(-1);
    } else if (step === 18 && by === 'manual') {
      navigateToStep(10);
    } else {
      if (step === 7) {
        const maxSubSteps = MOVEMENT_INSIGHT_SLUGS.length;
        const prevSlug = PROGRAM_GENERATOR_SLUGS[5]; // 'movement-insights'
        const newParams = new URLSearchParams(searchParams);
        newParams.set('step', prevSlug);
        newParams.set('subStep', MOVEMENT_INSIGHT_SLUGS[maxSubSteps - 1]);
        navigate(`${location.pathname}?${newParams.toString()}`);
        return;
      }
      if (step === 14) {
        const maxSubSteps = getSubStepSlugs(trainingGoal).length;
        const prevSlug = PROGRAM_GENERATOR_SLUGS[12]; // 'training-goal-details'
        const newParams = new URLSearchParams(searchParams);
        newParams.set('step', prevSlug);
        newParams.set('subStep', getSubStepSlugs(trainingGoal)[maxSubSteps - 1]);
        navigate(`${location.pathname}?${newParams.toString()}`);
        return;
      }
      navigateToStep(Math.max(step - 1, 2));
    }
  };

  const handleClientSelect = useCallback(
    (name: string, uid?: string, bodyProfile?: string) => {
      const store = useProgramGeneratorProgressStore.getState();
      if (uid && uid !== store.clientId) {
        store.resetProgress();
        store.setClientId(uid);
      } else if (uid) {
        store.setClientId(uid);
      }
      setClientName(name);
      if (bodyProfile) setClientBodyProfile(bodyProfile);
    },
    [setClientName, setClientBodyProfile],
  );

  const isNextDisabled = () => {
    if (isLoading) return true;
    switch (step) {
      case 2:
        return !clientId;
      case 3:
        return !energyLevel;
      case 7:
        return injuriesNotes.trim() !== '' && !/\p{L}/u.test(injuriesNotes);
      case 10:
        return howOftenTrain.length === 0 || !howOftenTrainReminder;
      case 11:
        return !workoutDuration;
      case 12:
        return !trainingGoal;
      case 13:
        if (trainingGoal === TrainingGoalSlug.IncreaseStrength) {
          if (currentSubStepIndex === 1) return increaseStrengthFocus.length === 0;
          if (currentSubStepIndex === 2) return !increaseStrengthOutcome;
          if (currentSubStepIndex === 3) return !increaseStrengthApproach;
        } else if (trainingGoal === TrainingGoalSlug.BuildMuscle) {
          if (currentSubStepIndex === 1) return buildMusclePrimary.length === 0;
          if (currentSubStepIndex === 2) return buildMuscleSecondary.length === 0;
          if (currentSubStepIndex === 3) return !buildMuscleApproach;
        } else if (trainingGoal === TrainingGoalSlug.WeightLoss) {
          if (currentSubStepIndex === 1) return !weightLossPrimary;
          if (currentSubStepIndex === 2) return weightLossSecondary.length === 0;
          if (currentSubStepIndex === 3) return !weightLossTarget;
          if (currentSubStepIndex === 4) return !weightLossApproach;
        } else if (trainingGoal === TrainingGoalSlug.Endurance) {
          if (currentSubStepIndex === 1) return !enduranceType;
          if (currentSubStepIndex === 2) return !enduranceApproach;
        } else if (trainingGoal === TrainingGoalSlug.Mobility) {
          if (currentSubStepIndex === 1) return mobilityArea.length === 0;
          if (currentSubStepIndex === 2) return !mobilityPurpose;
          if (currentSubStepIndex === 3) return !mobilityApproach;
        } else if (trainingGoal === TrainingGoalSlug.Performance) {
          if (currentSubStepIndex === 1) return !performanceSupport;
          if (currentSubStepIndex === 2) return !performanceFocus;
          if (currentSubStepIndex === 3) return !performanceApproach;
        } else if (trainingGoal === TrainingGoalSlug.GeneralFitness) {
          if (currentSubStepIndex === 1) return !generalFitnessMeaning;
          if (currentSubStepIndex === 2) return !generalFitnessApproach;
        } else if (trainingGoal === TrainingGoalSlug.Rehabilitation) {
          if (currentSubStepIndex === 1) {
            if (rehabilitationArea.length === 0) return true;
            const otherVal = rehabilitationArea.find((v: string) => v.startsWith('other:'));
            if (otherVal !== undefined) {
              const text = otherVal.replace('other:', '').trim();
              if (text === '' || !/\p{L}/u.test(text)) return true;
            }
            return false;
          }
          if (currentSubStepIndex === 2) return !rehabilitationStage;
          if (currentSubStepIndex === 3) return !rehabilitationApproach;
        } else if (trainingGoal === TrainingGoalSlug.InjuryPrevention) {
          if (currentSubStepIndex === 1) return injuryPreventionArea.length === 0;
          if (currentSubStepIndex === 2) return !injuryPreventionFocus;
          if (currentSubStepIndex === 3) return !injuryPreventionApproach;
        }
        return false;
      case 15:
        return !intensity;
      case 16:
        if (isEquipmentMandatory()) {
          return selectedEquipment.length === 0;
        }
        return false;
      default:
        return false;
    }
  };

  const isFirstStepOfSection = () => {
    if (step === 3) return true; // Client Profile
    if (step === 6 && movementInsightIndex === 1) return true; // Movement Insights
    if (step === 7) return true; // Injuries & Notes
    if (step === 10) return true; // Plan Structure
    if (step === 12) return true; // Program Goals
    if (step === 15) return true; // Intensity & Setup
    return false;
  };

  const isSaveDisabled = () => {
    // Check if the entire form is valid to allow returning to the review screen
    if (!clientId && by !== 'manual') return true;
    if (!energyLevel) return true;
    if (injuriesNotes.trim() !== '' && !/\p{L}/u.test(injuriesNotes)) return true;
    if (howOftenTrain.length === 0 || !howOftenTrainReminder) return true;
    if (!workoutDuration) return true;
    if (!trainingGoal) return true;

    if (trainingGoal === TrainingGoalSlug.IncreaseStrength) {
      if (
        increaseStrengthFocus.length === 0 ||
        !increaseStrengthOutcome ||
        !increaseStrengthApproach
      )
        return true;
    } else if (trainingGoal === TrainingGoalSlug.BuildMuscle) {
      if (
        buildMusclePrimary.length === 0 ||
        buildMuscleSecondary.length === 0 ||
        !buildMuscleApproach
      )
        return true;
    } else if (trainingGoal === TrainingGoalSlug.WeightLoss) {
      if (
        !weightLossPrimary ||
        weightLossSecondary.length === 0 ||
        !weightLossTarget ||
        !weightLossApproach
      )
        return true;
    } else if (trainingGoal === TrainingGoalSlug.Endurance) {
      if (!enduranceType || !enduranceApproach) return true;
    } else if (trainingGoal === TrainingGoalSlug.Mobility) {
      if (mobilityArea.length === 0 || !mobilityPurpose || !mobilityApproach) return true;
    } else if (trainingGoal === TrainingGoalSlug.Performance) {
      if (!performanceSupport || !performanceFocus || !performanceApproach) return true;
    } else if (trainingGoal === TrainingGoalSlug.GeneralFitness) {
      if (!generalFitnessMeaning || !generalFitnessApproach) return true;
    } else if (trainingGoal === TrainingGoalSlug.Rehabilitation) {
      let isRehabAreaValid = true;
      if (rehabilitationArea.length === 0) isRehabAreaValid = false;
      else {
        const otherVal = rehabilitationArea.find((v: string) => v.startsWith('other:'));
        if (otherVal) {
          const text = otherVal.replace('other:', '').trim();
          if (text === '' || !/\p{L}/u.test(text)) isRehabAreaValid = false;
        }
      }
      if (!isRehabAreaValid || !rehabilitationStage || !rehabilitationApproach) return true;
    } else if (trainingGoal === TrainingGoalSlug.InjuryPrevention) {
      if (injuryPreventionArea.length === 0 || !injuryPreventionFocus || !injuryPreventionApproach)
        return true;
    }

    if (!intensity) return true;
    if (isEquipmentMandatory() && selectedEquipment.length === 0) return true;
    return false;
  };

  const isLastStepOfSection = () => {
    if (step === 5) return true; // Client Profile
    if (step === 6 && movementInsightIndex === 5) return true; // Movement Insights
    if (step === 7) return true; // Injuries & Notes
    if (step === 11) return true; // Plan Structure
    if (step === 14) return true; // Program Goals
    if (step === 16) return true; // Intensity & Setup
    return false;
  };

  const getGoalPriority = () => {
    switch (trainingGoal) {
      case TrainingGoalSlug.IncreaseStrength:
        return increaseStrengthFocus;
      case TrainingGoalSlug.BuildMuscle:
        return buildMusclePrimary;
      case TrainingGoalSlug.WeightLoss:
        return weightLossPrimary ? [weightLossPrimary] : [];
      case TrainingGoalSlug.Endurance:
        return enduranceType ? [enduranceType] : [];
      case TrainingGoalSlug.Mobility:
        return mobilityArea;
      case TrainingGoalSlug.Performance:
        return performanceSupport ? [performanceSupport] : [];
      case TrainingGoalSlug.GeneralFitness:
        return generalFitnessMeaning ? [generalFitnessMeaning] : [];
      case TrainingGoalSlug.Rehabilitation:
        return rehabilitationArea;
      case TrainingGoalSlug.InjuryPrevention:
        return injuryPreventionArea;
      default:
        return [];
    }
  };

  const getGoalSecondary = () => {
    switch (trainingGoal) {
      case TrainingGoalSlug.IncreaseStrength:
        return increaseStrengthOutcome ? [increaseStrengthOutcome] : [];
      case TrainingGoalSlug.BuildMuscle:
        return buildMuscleSecondary;
      case TrainingGoalSlug.WeightLoss:
        return weightLossSecondary;
      case TrainingGoalSlug.Endurance:
        return enduranceApproach ? [enduranceApproach] : [];
      case TrainingGoalSlug.Mobility:
        return mobilityPurpose ? [mobilityPurpose] : [];
      case TrainingGoalSlug.Performance:
        return performanceFocus ? [performanceFocus] : [];
      case TrainingGoalSlug.GeneralFitness:
        return generalFitnessApproach ? [generalFitnessApproach] : [];
      case TrainingGoalSlug.Rehabilitation:
        return rehabilitationStage ? [rehabilitationStage] : [];
      case TrainingGoalSlug.InjuryPrevention:
        return injuryPreventionFocus ? [injuryPreventionFocus] : [];
      default:
        return [];
    }
  };

  const getGoalApproach = () => {
    switch (trainingGoal) {
      case TrainingGoalSlug.IncreaseStrength:
        return increaseStrengthApproach;
      case TrainingGoalSlug.BuildMuscle:
        return buildMuscleApproach;
      case TrainingGoalSlug.WeightLoss:
        return weightLossApproach;
      case TrainingGoalSlug.Mobility:
        return mobilityApproach;
      case TrainingGoalSlug.Performance:
        return performanceApproach;
      case TrainingGoalSlug.GeneralFitness:
        return generalFitnessApproach;
      case TrainingGoalSlug.Rehabilitation:
        return rehabilitationApproach;
      case TrainingGoalSlug.InjuryPrevention:
        return injuryPreventionApproach;
      case TrainingGoalSlug.Endurance:
      default:
        return '';
    }
  };

  const isProgressLoading = !!urlProgramId && (isLoadingProgram || !hasResumed);

  if (isProgressLoading) {
    return (
      <div
        className="dashboard-screen program-generator-ai-screen flex-center-column"
        style={{
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: 'transparent',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  const getProgressPercentage = () => {
    if (by === 'manual') {
      if (step === 2) return 33;
      if (step === 9) return 66;
      if (step === 10) return 100;
      return 0;
    }

    let effectiveStep = step;

    if (step === 6) {
      effectiveStep = step + (movementInsightIndex - 1) / 5;
    } else if (step === 13) {
      const maxSubSteps = getSubStepSlugs(trainingGoal).length;
      if (maxSubSteps > 0) {
        effectiveStep = step + (currentSubStepIndex - 1) / maxSubSteps;
      }
    }

    if (effectiveStep < 9) {
      return Math.min(((effectiveStep - 1) / 7) * 100, 100);
    } else {
      return Math.min(((effectiveStep - 8) / (totalSteps - 8)) * 100, 100);
    }
  };

  return (
    <div
      className={`dashboard-screen program-generator-ai-screen ${step >= 2 && step !== 8 ? 'step-2-active' : ''}`}
    >
      <div className="hero-glow-video-container flex-center-column" ref={videoContainerRef}>
        <video
          ref={videoRef}
          src={MiniPTGIF}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="hero-glow-video"
          onLoadedData={(e) => {
            e.currentTarget.muted = true;
            e.currentTarget.play().catch(() => {});
          }}
        />
        {(step === 1 || step === 8) && <span className="mini-pt-text">miniPT Core</span>}
        {step >= 2 && step !== 8 && <span className="mini-pt-text white-50">miniPT</span>}
      </div>

      {step === 1 && (
        <div className="animation-content max-width-768 animation-text-position">
          <TextAnimate
            animation="slideUp"
            by="word"
            as="h2"
            center={true}
            className="page-intro-title mt-0"
            duration={1}
            delay={0.05}
            onComplete={handleAnimationComplete}
          >
            {`Hi! I'm miniPT Core and I'll\ntake care of building your\nworkout program for you.`}
          </TextAnimate>
        </div>
      )}

      {step === 8 && (
        <div className="animation-content-8 max-width-768 animation-text-position flex-col-gap-8">
          <TextAnimate
            animation="slideUp"
            by="word"
            as="h2"
            center={true}
            className="page-intro-title mt-0"
            duration={1}
            delay={0.05}
            onComplete={handleAnimation8Complete}
          >
            {`Great! Now let's talk about the program`}
          </TextAnimate>
        </div>
      )}

      {step === 2 && (
        <div className="step-content">
          <ClientSelectionStep
            onClientSelect={handleClientSelect}
            onNext={handleNext}
            value={clientId}
            isLoading={isLoading || isCheckingProfile || isCheckingPrograms}
          />
        </div>
      )}

      {step === 2.1 && (
        <div className="step-content">
          <OnboardNewClientPromptStep
            onNext={() => setStep(3)} // Temporary functionality
            onLater={() => {}} // Temporary functionality
          />
        </div>
      )}

      {step === 3 && (
        <div className="step-content">
          <EnergyLevelStep
            clientName={clientName.split(' ')[0]}
            value={energyLevel}
            onChange={(val) => setEnergyLevel(val)}
            onNext={handleNext}
          />
        </div>
      )}

      {step === 4 && (
        <div className="step-content">
          <ActivityLevelStep
            clientName={clientName.split(' ')[0]}
            value={activityLevel}
            onChange={(val) => setActivityLevel(val)}
          />
        </div>
      )}

      {step === 5 && (
        <div className="step-content">
          <FitnessLevelStep
            clientName={clientName.split(' ')[0]}
            value={fitnessLevel}
            onChange={(val) => setFitnessLevel(val)}
          />
        </div>
      )}

      {step === 6 && (
        <div className="step-content">
          <MovementInsightsStep
            insightIndex={movementInsightIndex}
            values={movementInsights}
            onChange={(vals) => setMovementInsights(vals)}
          />
        </div>
      )}

      {step === 7 && (
        <div className="step-content">
          <InjuriesLimitationsStep
            clientName={clientName.split(' ')[0]}
            bodyProfile={clientBodyProfile}
            selectedParts={injuriesParts}
            onChangeParts={setInjuriesParts}
            notes={injuriesNotes}
            onChangeNotes={setInjuriesNotes}
          />
        </div>
      )}

      {step === 9 && (
        <div className="step-content">
          <ProgramDurationStep
            clientName={clientName.split(' ')[0]}
            value={programDuration}
            onChange={(val) => setProgramDuration(val)}
          />
        </div>
      )}

      {step === 10 && (
        <div className="step-content">
          <HowOftenTrainStep
            clientName={clientName.split(' ')[0]}
            value={howOftenTrain}
            reminderValue={howOftenTrainReminder}
            onChange={(days, reminder) => {
              setHowOftenTrain(days);
              setHowOftenTrainReminder(reminder);
            }}
          />
        </div>
      )}

      {step === 11 && (
        <div className="step-content">
          <WorkoutDurationStep
            value={workoutDuration}
            onChange={(val) => setWorkoutDuration(val)}
            onNext={handleNext}
          />
        </div>
      )}

      {step === 12 && (
        <div className="step-content">
          <TrainingGoalStep
            value={trainingGoal}
            onNext={(goal) => {
              setTrainingGoal(goal);
              handleNext(goal);
            }}
          />
        </div>
      )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.IncreaseStrength &&
        currentSubStepIndex === 1 && (
          <div className="step-content increase_strength_strength">
            <IncreaseStrengthStep1
              clientBodyProfile={clientBodyProfile}
              value={increaseStrengthFocus}
              onChange={(val) => setIncreaseStrengthFocus(val)}
            />
          </div>
        )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.IncreaseStrength &&
        currentSubStepIndex === 2 && (
          <div className="step-content increase_strength_what_strength">
            <IncreaseStrengthStep2
              value={increaseStrengthOutcome}
              onNext={(val) => {
                setIncreaseStrengthOutcome(val);
                handleNext();
              }}
            />
          </div>
        )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.IncreaseStrength &&
        currentSubStepIndex === 3 && (
          <div className="step-content goal_approached">
            <IncreaseStrengthStep3
              value={increaseStrengthApproach}
              onNext={(val) => {
                setIncreaseStrengthApproach(val);
                handleNext();
              }}
            />
          </div>
        )}

      {step === 13 && trainingGoal === TrainingGoalSlug.Endurance && currentSubStepIndex === 1 && (
        <div className="step-content endurance_what_strength">
          <EnduranceStep1
            value={enduranceType}
            onNext={(val) => {
              setEnduranceType(val);
              handleNext();
            }}
          />
        </div>
      )}

      {step === 13 && trainingGoal === TrainingGoalSlug.Endurance && currentSubStepIndex === 2 && (
        <div className="step-content goal_approached">
          <EnduranceStep2
            value={enduranceApproach}
            onNext={(val) => {
              setEnduranceApproach(val);
              handleNext();
            }}
          />
        </div>
      )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.Performance &&
        currentSubStepIndex === 1 && (
          <div className="step-content performance_what_strength">
            <PerformanceStep1
              value={performanceSupport}
              onNext={(val) => {
                setPerformanceSupport(val);
                handleNext();
              }}
            />
          </div>
        )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.Performance &&
        currentSubStepIndex === 2 && (
          <div className="step-content performance_focus">
            <PerformanceStep2
              value={performanceFocus}
              onNext={(val) => {
                setPerformanceFocus(val);
                handleNext();
              }}
            />
          </div>
        )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.Performance &&
        currentSubStepIndex === 3 && (
          <div className="step-content performance_goal_approached goal_approached">
            <PerformanceStep3
              value={performanceApproach}
              onNext={(val) => {
                setPerformanceApproach(val);
                handleNext();
              }}
            />
          </div>
        )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.GeneralFitness &&
        currentSubStepIndex === 1 && (
          <div className="step-content general_fitness_what_strength">
            <GeneralFitnessStep1
              value={generalFitnessMeaning}
              onNext={(val) => {
                setGeneralFitnessMeaning(val);
                handleNext();
              }}
            />
          </div>
        )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.GeneralFitness &&
        currentSubStepIndex === 2 && (
          <div className="step-content general_fitness_goal_approached goal_approached">
            <GeneralFitnessStep2
              value={generalFitnessApproach}
              onNext={(val) => {
                setGeneralFitnessApproach(val);
                handleNext();
              }}
            />
          </div>
        )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.InjuryPrevention &&
        currentSubStepIndex === 1 && (
          <div className="step-content injury_prevention_protect">
            <InjuryPreventionStep1
              clientBodyProfile={clientBodyProfile}
              value={injuryPreventionArea}
              onChange={(val) => setInjuryPreventionArea(val)}
            />
          </div>
        )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.InjuryPrevention &&
        currentSubStepIndex === 2 && (
          <div className="step-content injury_prevention_focus">
            <InjuryPreventionStep2
              value={injuryPreventionFocus}
              onNext={(val) => {
                setInjuryPreventionFocus(val);
                handleNext();
              }}
            />
          </div>
        )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.InjuryPrevention &&
        currentSubStepIndex === 3 && (
          <div className="step-content injury_prevention_goal_approached goal_approached ">
            <InjuryPreventionStep3
              value={injuryPreventionApproach}
              onNext={(val) => {
                setInjuryPreventionApproach(val);
                handleNext();
              }}
            />
          </div>
        )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.Rehabilitation &&
        currentSubStepIndex === 1 && (
          <div className="step-content rehabilitation_support">
            <RehabilitationStep1
              clientBodyProfile={clientBodyProfile}
              value={rehabilitationArea}
              onChange={(val) => setRehabilitationArea(val)}
            />
          </div>
        )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.Rehabilitation &&
        currentSubStepIndex === 2 && (
          <div className="step-content rehabilitation_support_stage">
            <RehabilitationStep2
              value={rehabilitationStage}
              onNext={(val) => {
                setRehabilitationStage(val);
                handleNext();
              }}
            />
          </div>
        )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.Rehabilitation &&
        currentSubStepIndex === 3 && (
          <div className="step-content rehabilitation_goal_approached goal_approached">
            <RehabilitationStep3
              value={rehabilitationApproach}
              onNext={(val) => {
                setRehabilitationApproach(val);
                handleNext();
              }}
            />
          </div>
        )}

      {step === 13 && trainingGoal === TrainingGoalSlug.Mobility && currentSubStepIndex === 1 && (
        <div className="step-content mobility_focus">
          <MobilityStep1
            value={mobilityArea}
            clientBodyProfile={clientBodyProfile}
            onChange={(val) => setMobilityArea(val)}
          />
        </div>
      )}

      {step === 13 && trainingGoal === TrainingGoalSlug.Mobility && currentSubStepIndex === 2 && (
        <div className="step-content mobility_purpose">
          <MobilityStep2
            value={mobilityPurpose}
            onNext={(val) => {
              setMobilityPurpose(val);
              handleNext();
            }}
          />
        </div>
      )}

      {step === 13 && trainingGoal === TrainingGoalSlug.Mobility && currentSubStepIndex === 3 && (
        <div className="step-content mobility_goal_approached goal_approached">
          <MobilityStep3
            value={mobilityApproach}
            onNext={(val) => {
              setMobilityApproach(val);
              handleNext();
            }}
          />
        </div>
      )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.BuildMuscle &&
        currentSubStepIndex === 1 && (
          <div className="step-content main_area">
            <BuildMuscleStep1
              clientBodyProfile={clientBodyProfile}
              value={buildMusclePrimary}
              onChange={(val) => {
                setBuildMusclePrimary(val);
              }}
            />
          </div>
        )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.BuildMuscle &&
        currentSubStepIndex === 2 && (
          <div className="step-content others_area">
            <BuildMuscleStep2
              clientBodyProfile={clientBodyProfile}
              value={buildMuscleSecondary}
              excludedAreas={buildMusclePrimary}
              onChange={(val) => setBuildMuscleSecondary(val)}
            />
          </div>
        )}

      {step === 13 &&
        trainingGoal === TrainingGoalSlug.BuildMuscle &&
        currentSubStepIndex === 3 && (
          <div className="step-content build_muscle_goal_approached goal_approached">
            <BuildMuscleStep3
              value={buildMuscleApproach}
              onNext={(val) => {
                setBuildMuscleApproach(val);
                handleNext();
              }}
            />
          </div>
        )}

      {step === 13 && trainingGoal === TrainingGoalSlug.WeightLoss && currentSubStepIndex === 1 && (
        <div className="step-content main_area">
          <WeightLossStep1
            clientBodyProfile={clientBodyProfile}
            value={weightLossPrimary}
            onNext={(val) => {
              setWeightLossPrimary(val);
              handleNext(val);
            }}
          />
        </div>
      )}

      {step === 13 && trainingGoal === TrainingGoalSlug.WeightLoss && currentSubStepIndex === 2 && (
        <div className="step-content others_area">
          <WeightLossStep2
            clientBodyProfile={clientBodyProfile}
            value={weightLossSecondary}
            excludedArea={weightLossPrimary}
            onChange={(val) => {
              setWeightLossSecondary(val);
            }}
          />
        </div>
      )}

      {step === 13 && trainingGoal === TrainingGoalSlug.WeightLoss && currentSubStepIndex === 3 && (
        <div className="step-content">
          <WeightLossStep3
            value={weightLossTarget}
            onChange={(val) => {
              setWeightLossTarget(val);
            }}
          />
        </div>
      )}

      {step === 13 && trainingGoal === TrainingGoalSlug.WeightLoss && currentSubStepIndex === 4 && (
        <div className="step-content weight_loss_goal_approached goal_approached">
          <WeightLossStep4
            value={weightLossApproach}
            onNext={(val) => {
              setWeightLossApproach(val);
              handleNext();
            }}
          />
        </div>
      )}

      {step === 14 && (
        <div className="step-content">
          <SecondaryTrainingGoalStep
            value={secondaryGoals}
            primaryGoal={trainingGoal}
            onChange={(goals) => {
              setSecondaryGoals(goals);
            }}
          />
        </div>
      )}

      {step === 15 && (
        <div className="step-content">
          <IntensityVibeStep
            clientName={clientName.split(' ')[0]}
            value={intensity}
            onChange={(val) => setIntensity(val)}
            onNext={handleNext}
          />
        </div>
      )}

      {step === 16 && (
        <div className="step-content">
          <SelectEquipmentStep
            value={selectedEquipment}
            isMandatory={isEquipmentMandatory()}
            onChange={(equipment) => {
              setSelectedEquipment(equipment);
            }}
          />
        </div>
      )}

      {step === 17 && (
        <div className="step-content">
          <ReviewStep
            onEdit={handleEditStep}
            onGenerate={(payload) => {
              const programId = useProgramGeneratorProgressStore.getState().programId || '';
              navigate(`/onboarding-trainer/dashboard/program-generated/${programId}`, {
                state: { payload },
              });
            }}
            clientName={clientName.split(' ')[0]}
            clientBodyProfile={clientBodyProfile}
            energyLevel={energyLevel}
            activityLevel={activityLevel}
            fitnessLevel={fitnessLevel}
            movementInsights={movementInsights}
            injuriesParts={injuriesParts}
            injuriesNotes={injuriesNotes}
            programDuration={programDuration}
            howOftenTrain={howOftenTrain}
            howOftenTrainReminder={howOftenTrainReminder}
            workoutDuration={workoutDuration}
            trainingGoal={trainingGoal}
            intensity={intensity}
            selectedEquipment={selectedEquipment}
            goalPriority={getGoalPriority()}
            goalSecondary={getGoalSecondary()}
            goalApproach={getGoalApproach()}
            secondaryGoals={secondaryGoals}
          />
        </div>
      )}

      {step === 18 && by === 'manual' && (
        <div className="step-content flex-center-column">
          <ProgramNameStep
            value={programName}
            onChange={(val) => setProgramName(val)}
            onNext={handleNext}
          />
        </div>
      )}

      {step >= 2 && step !== 8 && step !== 17 && step !== 18 && (
        <div className="wizard-footer">
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${getProgressPercentage()}%`,
                }}
              ></div>
            </div>
          </div>
          <div className="actions">
            {step > 2 && (!isEditingFromReview || !isFirstStepOfSection()) && (
              <Button
                variant="outline"
                className="back-btn align-items-center m-height-48"
                onClick={handleBack}
                svg={<BackIcon />}
              >
                {'Back'}
              </Button>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              {(!isEditingFromReview || !isLastStepOfSection() || hasIncompleteStepAhead()) && (
                <Button
                  variant={
                    (step === 7 && injuriesParts.length === 0 && injuriesNotes.trim() === '') ||
                    (step === 14 && secondaryGoals.length === 0) ||
                    (step === 16 && selectedEquipment.length === 0 && !isEquipmentMandatory())
                      ? 'outline'
                      : 'primary'
                  }
                  className={`next-btn right-arrow m-height-48 ${(step === 7 && injuriesParts.length === 0 && injuriesNotes.trim() === '') || (step === 14 && secondaryGoals.length === 0) || (step === 16 && selectedEquipment.length === 0 && !isEquipmentMandatory()) ? 'align-items-center' : 'btn-primary-white'}`}
                  onClick={handleNext}
                  svg={<NextIcon />}
                  disabled={isNextDisabled() || isCheckingPrograms || isCheckingProfile}
                  isLoading={isLoading || isCheckingPrograms || isCheckingProfile}
                >
                  {(step === 7 && injuriesParts.length === 0 && injuriesNotes.trim() === '') ||
                  (step === 14 && secondaryGoals.length === 0) ||
                  (step === 16 && selectedEquipment.length === 0 && !isEquipmentMandatory()) ? (
                    'Skip This'
                  ) : (
                    <span style={{ color: '#000', fontWeight: 'bold' }}>Next</span>
                  )}
                </Button>
              )}
              {isEditingFromReview && (
                <Button
                  variant="primary"
                  className="save-btn align-items-center m-height-48"
                  onClick={handleSaveEdit}
                  disabled={isLoading || isSaveDisabled()}
                  isLoading={isLoading}
                >
                  <span style={{ color: '#000', fontWeight: 'bold' }}>Save</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <Modal
        open={blocker.state === 'blocked'}
        footer={null}
        closable={false}
        centered
        width={518}
        rootClassName="logout-modal"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="logout-modal-content">
          <div className="logout-icon-wrapper">{confirmationSVG?.draft}</div>
          <h2>
            {onboardingOnly ? 'Client fitness profile saved' : 'Your program draft has been saved'}
          </h2>
          <p>
            {onboardingOnly
              ? "The client's fitness profile has been successfully saved."
              : 'You can continue editing this program from the Programs page.'}
          </p>
          <div className="draft-saved-actions">
            <button
              className="btn-back"
              onClick={() => blocker.state === 'blocked' && blocker.reset()}
            >
              {confirmationSVG?.back} Back to The Form
            </button>
            <button
              className="btn-proceed"
              onClick={() => {
                if (blocker.state === 'blocked') {
                  const { location: nextLocation } = blocker;
                  const currentLocation = window.location;

                  if (
                    nextLocation.search.includes('action=new_program') &&
                    !currentLocation.search.includes('action=new_program')
                  ) {
                    blocker.reset();
                    navigate('/onboarding-trainer/dashboard/');
                  } else {
                    blocker.proceed();
                  }
                }
              }}
            >
              Great, Thanks!
            </button>
          </div>
        </div>
      </Modal>

      <ReplaceProgramModal
        isOpen={showReplaceProgramModal}
        onKeep={() => setShowReplaceProgramModal(false)}
        isReplacing={isReplacingProgram}
        onReplace={async () => {
          setIsReplacingProgram(true);
          try {
            await Promise.all(
              clientPrograms.map((p) =>
                deleteProgram({ docPath: p.docPath, hideToast: true }).catch(console.error),
              ),
            );
            setShowReplaceProgramModal(false);
            const cid = useProgramGeneratorProgressStore.getState().clientId!;
            if (by === 'manual') {
              navigateToStep(9);
            } else {
              const nextStep = await checkProfile(cid);
              navigateToStep(Math.min(nextStep, totalSteps));
            }
          } finally {
            setIsReplacingProgram(false);
          }
        }}
      />
    </div>
  );
};
